import { FC, useRef, useState } from 'react'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { ReactComponent as TotalChatsIcon } from 'src/assets/svg/inbox-analysis-total-chats.svg'
import { ReactComponent as TotalMessagesIcon } from 'src/assets/svg/total-messages-inbox-analysis.svg'
import { ReactComponent as NewVsReturningIcon } from 'src/assets/svg/inbox-analysis-new-vs-returning.svg'
import { ReactComponent as OrganicVsPaidIcon } from 'src/assets/svg/inbox-analysis-organic-vs-paid.svg'
import { ReactComponent as TotalUsersIcon } from 'src/assets/svg/inbox-analysis-total-users.svg'
import { ReactComponent as TotalSourceIcon } from 'src/assets/svg/total-source-global.svg'
import { ReactComponent as TopicIcon } from 'src/assets/svg/inbox-analysis-top-topic.svg'
import { Skeleton } from 'src/components/Skeleton'
import { TrendBadge } from 'src/components/TrendBadge'
import { useGetInboxAvailablePlatformsQuery } from 'src/reactQuery/useGetInboxAvailablePlatformsQuery'
import { useGetInboxOverviewQuery } from 'src/reactQuery/useGetInboxOverviewQuery'
import { TopDayCard } from 'src/pages/Campaigns/HeroSection/Cards/TopDayCard'
import { TopKeywordCard } from 'src/pages/Campaigns/HeroSection/Cards/TopKeywordCard'
import { TopSourceCard } from 'src/pages/Campaigns/HeroSection/Cards/TopSourceCard'
import classes from './inboxAnalysisOverview.module.scss'
import { OverviewStatCard } from './OverviewStatCard'
import { SentimentSplit } from './SentimentSplit'
import { SourcesSplit } from './SourcesSplit'
import { TwoWaySplit } from './TwoWaySplit'

const PLATFORM_OPTIONS = [
  { id: 'all', label: 'All Platforms' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'booking', label: 'Booking' },
  { id: 'expedia', label: 'Expedia' },
] as const

const OPERATOR_OPTIONS = [
  'Luka Vadachkoria',
  'Anna Vadachkoria',
  'Saba Vadachkoria',
  'Gela Vadachkoria',
  'Liza Vadachkoria',
  'Nino Vadachkoria',
  'Mate Vadachkoria',
]

const FALLBACK_OPERATOR_OPTIONS = OPERATOR_OPTIONS.map((operator) => ({
  id: operator,
  name: operator,
  value: operator,
}))

interface InboxAnalysisOverviewProps {
  fromDate?: string
  pageId: string
  toDate?: string
}

// Three rows of 3 x 366px cards: Chats / Messages / Sources (224px tall),
// Users / New vs Returning / Organic vs Paid (152px) and the Top Source / Day /
// Topic hero cards (152px) — see .campaignCardsGrid. The skeletons take each
// card's exact size, so nothing shifts when the data arrives; they replace
// cards that otherwise render their own "no data" state while the first
// request is still in flight.
const OVERVIEW_CARD_WIDTH = 366
const OVERVIEW_ROW_HEIGHTS = [224, 152, 152]
const OVERVIEW_CARD_RADIUS = 16
const OVERVIEW_CARDS_PER_ROW = 3

// New vs Returning and Organic vs Paid palettes (from the design).
const NEW_COLOR = '#7777f6'
const RETURNING_COLOR = '#68717d'
const ORGANIC_COLOR = '#237b68'
const PAID_COLOR = '#b85d18'

export const InboxAnalysisOverview: FC<InboxAnalysisOverviewProps> = ({
  fromDate,
  pageId,
  toDate,
}) => {
  const { data: availablePlatforms } = useGetInboxAvailablePlatformsQuery({
    fromDate,
    pageId,
    toDate,
  })
  const platformOptions = PLATFORM_OPTIONS.filter(
    (platform) =>
      platform.id === 'all' || (availablePlatforms ?? []).includes(platform.id)
  )

  const [activePlatform, setActivePlatform] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<'platforms' | 'operators'>(
    'platforms'
  )
  const [isOperatorDropdownOpen, setIsOperatorDropdownOpen] = useState(false)
  const [operatorSearch, setOperatorSearch] = useState('')
  const operatorDropdownRef = useRef<HTMLDivElement>(null)

  // Close the operator dropdown when clicking anywhere outside of it.
  useOutsideClick({
    onOutsideClick: () => setIsOperatorDropdownOpen(false),
    ref: operatorDropdownRef,
  })
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<
    string[] | null
  >(null)

  const { data: operatorOptionsData } = useGetInboxOverviewQuery({
    fromDate,
    groupBy: 'operators',
    pageId,
    toDate,
  })

  const operatorOptions = operatorOptionsData?.groups.items.length
    ? operatorOptionsData.groups.items
        .filter(
          (operator) =>
            operator.id !== 'op-unassigned' &&
            operator.id !== 'Unassigned' &&
            operator.name !== 'Unassigned'
        )
        .map((operator) => ({
          id: operator.id,
          name: operator.name,
          value: operator.name || operator.id,
        }))
    : FALLBACK_OPERATOR_OPTIONS

  const normalizedOperatorSearch = operatorSearch.trim().toLowerCase()
  const visibleOperatorOptions = normalizedOperatorSearch
    ? operatorOptions.filter((operator) =>
        (operator.name || '').toLowerCase().includes(normalizedOperatorSearch)
      )
    : operatorOptions

  const selectedOperatorFilter =
    activeTab === 'operators' && selectedOperatorIds !== null
      ? selectedOperatorIds.length
        ? selectedOperatorIds.join(',')
        : '__none__'
      : undefined

  const { data, isFetching, isLoading } = useGetInboxOverviewQuery({
    fromDate,
    groupBy: activeTab,
    operatorIds: selectedOperatorFilter,
    pageId,
    platforms:
      activeTab === 'platforms' && activePlatform !== 'all'
        ? activePlatform
        : undefined,
    toDate,
  })

  // On the Operators tab the main query is grouped by operators, but the
  // "Total Sources" card must still show platforms (the platforms the
  // selected operators' data comes from), so fetch a platforms-grouped
  // dataset filtered by the selected operators.
  const { data: platformSourceData } = useGetInboxOverviewQuery({
    enabled: activeTab === 'operators',
    fromDate,
    groupBy: 'platforms',
    operatorIds: selectedOperatorFilter,
    pageId,
    toDate,
  })

  // First load only — every card takes `hasData` from `data`, so before the
  // first response each one renders its own empty state, which reads as a
  // confident "no data" for a request that hasn't answered yet. A refetch keeps
  // the real values on screen and only dims them.
  const isInitialLoading = isLoading && !data

  const sourceGroups =
    activeTab === 'operators' ? platformSourceData?.groups : data?.groups

  const totalChats = data?.totalChats
  const totalMessages = data?.totalMessages
  const sourceSplit = data?.sourceSplit ?? null
  const comparison = data?.comparison
  // On the Operators tab the sources (and so the new ones) come from the
  // platforms-grouped query.
  const newSources =
    (activeTab === 'operators' ? platformSourceData : data)?.comparison?.sources
      .newSources ?? []
  const topPositiveGroup = data?.topGroup.mostPositive
  const topNegativeGroup = data?.topGroup.mostNegative
  // This page counts conversations, so every card's unit is "Chats".
  const sourceUnitLabel = 'Chats'
  const positiveDay = data?.topDay.mostPositive
  const negativeDay = data?.topDay.mostNegative
  const positiveTopic = data?.topTopic.mostPositive
  const negativeTopic = data?.topTopic.mostNegative
  const selectedOperatorCount =
    selectedOperatorIds === null
      ? operatorOptions.length
      : selectedOperatorIds.length
  const operatorDropdownPrefix =
    selectedOperatorIds === null
      ? 'All'
      : selectedOperatorCount === 0
        ? 'No'
        : String(selectedOperatorCount)
  const operatorDropdownLabel =
    selectedOperatorIds === null
      ? 'Operators Selected'
      : selectedOperatorCount === 1
        ? 'Operator Selected'
        : 'Operators Selected'

  const toggleOperator = (operatorId: string) => {
    setSelectedOperatorIds((current) => {
      const currentSelection =
        current === null
          ? operatorOptions.map((operator) => operator.value)
          : current
      const nextSelection = currentSelection.includes(operatorId)
        ? currentSelection.filter((item) => item !== operatorId)
        : [...currentSelection, operatorId]

      if (
        nextSelection.length === operatorOptions.length &&
        operatorOptions.length > 0
      ) {
        return null
      }

      return nextSelection
    })
  }

  return (
    <div className={classes.overview}>
      <div className={classes.headerSection}>
        <div className={classes.header}>
          <h2 className={classes.title}>Overview</h2>
          <div className={classes.tabsContainer}>
            <button
              className={`${classes.tab} ${
                activeTab === 'platforms' ? classes.active : ''
              }`}
              onClick={() => {
                setActiveTab('platforms')
                setIsOperatorDropdownOpen(false)
              }}
              type="button"
            >
              Platforms
            </button>
            <button
              className={`${classes.tab} ${
                activeTab === 'operators' ? classes.active : ''
              }`}
              onClick={() => setActiveTab('operators')}
              type="button"
            >
              Operators
            </button>
          </div>
        </div>

        {activeTab === 'platforms' && (
          <div className={classes.platformTabs}>
            {platformOptions.map((platform) => (
              <button
                className={`${classes.platformTab} ${
                  activePlatform === platform.id ? classes.activePlatform : ''
                }`}
                key={platform.id}
                onClick={() => setActivePlatform(platform.id)}
                type="button"
              >
                {platform.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'operators' && (
          <div
            className={classes.operatorDropdownWrap}
            ref={operatorDropdownRef}
          >
            {!isOperatorDropdownOpen && (
              <button
                className={classes.operatorDropdown}
                onClick={() => setIsOperatorDropdownOpen(true)}
                type="button"
              >
                <span className={classes.operatorDropdownLabel}>Operator</span>
                <span className={classes.operatorDropdownValue}>
                  <span className={classes.operatorDropdownValueAccent}>
                    {operatorDropdownPrefix}
                  </span>
                  {operatorDropdownLabel}
                  <svg
                    aria-hidden="true"
                    className={classes.operatorDropdownIcon}
                    fill="none"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </span>
              </button>
            )}

            {isOperatorDropdownOpen && (
              <div className={classes.operatorDropdownPanel}>
                <div className={classes.operatorSearchRow}>
                  <span className={classes.operatorSearchLabel}>
                    <svg
                      aria-hidden="true"
                      className={classes.operatorSearchIcon}
                      fill="none"
                      viewBox="0 0 14 14"
                    >
                      <circle
                        cx="6.125"
                        cy="6.125"
                        r="4.875"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M9.75 9.75L12.25 12.25"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeWidth="1.5"
                      />
                    </svg>
                    <input
                      autoFocus
                      className={classes.operatorSearchInput}
                      onChange={(event) =>
                        setOperatorSearch(event.target.value)
                      }
                      placeholder="Operator"
                      type="text"
                      value={operatorSearch}
                    />
                  </span>
                  <button
                    className={classes.operatorDropdownValue}
                    onClick={() => setIsOperatorDropdownOpen(false)}
                    type="button"
                  >
                    <span className={classes.operatorDropdownValueAccent}>
                      {operatorDropdownPrefix}
                    </span>
                    {operatorDropdownLabel}
                    <svg
                      aria-hidden="true"
                      className={classes.operatorDropdownIcon}
                      fill="none"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M5 12.5L10 7.5L15 12.5"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </div>

                <div className={classes.operatorList}>
                  {visibleOperatorOptions.length === 0 && (
                    <div className={classes.operatorEmpty}>
                      No operators found
                    </div>
                  )}
                  {visibleOperatorOptions.map((operator) => {
                    const isSelected =
                      selectedOperatorIds === null ||
                      selectedOperatorIds.includes(operator.value)

                    return (
                      <button
                        className={`${classes.operatorOption} ${
                          isSelected ? classes.operatorOptionActive : ''
                        }`}
                        key={operator.id}
                        onClick={() => toggleOperator(operator.value)}
                        type="button"
                      >
                        <span className={classes.operatorCheckbox}>
                          {isSelected && (
                            <svg
                              aria-hidden="true"
                              fill="none"
                              viewBox="0 0 16 16"
                            >
                              <path
                                d="M3.5 8.2L6.5 11L12.5 5"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.8"
                              />
                            </svg>
                          )}
                        </span>
                        <span>{operator.name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className={classes.campaignCardsGrid}
        // Dim only when refetching over values that are already on screen —
        // dimming the skeletons would just wash out their shimmer.
        style={{ opacity: isFetching && !isInitialLoading ? 0.6 : 1 }}
      >
        {isInitialLoading &&
          OVERVIEW_ROW_HEIGHTS.flatMap((height, row) =>
            Array.from({ length: OVERVIEW_CARDS_PER_ROW }).map((_, column) => (
              <Skeleton
                height={height}
                // eslint-disable-next-line react/no-array-index-key
                key={`${row}-${column}`}
                radius={OVERVIEW_CARD_RADIUS}
                width={OVERVIEW_CARD_WIDTH}
              />
            ))
          )}

        {!isInitialLoading && (
          <>
            <OverviewStatCard
              badge={
                <TrendBadge
                  previousFromDate={comparison?.previousFromDate}
                  previousToDate={comparison?.previousToDate}
                  value={comparison?.totalChats.changePercent}
                />
              }
              icon={TotalChatsIcon}
              size="large"
              title="Total Chats"
              value={totalChats?.total ?? 0}
            >
              {totalChats && <SentimentSplit breakdown={totalChats} />}
            </OverviewStatCard>
            <OverviewStatCard
              badge={
                <TrendBadge
                  previousFromDate={comparison?.previousFromDate}
                  previousToDate={comparison?.previousToDate}
                  value={comparison?.totalMessages.changePercent}
                />
              }
              icon={TotalMessagesIcon}
              size="large"
              title="Total Messages"
              value={totalMessages?.total ?? 0}
            >
              {totalMessages && <SentimentSplit breakdown={totalMessages} />}
            </OverviewStatCard>
            <OverviewStatCard
              badge={
                newSources.length > 0 && (
                  <TrendBadge
                    label={`${newSources.length} new source${
                      newSources.length === 1 ? '' : 's'
                    }`}
                    value={newSources.length}
                  />
                )
              }
              icon={TotalSourceIcon}
              size="large"
              title="Total Sources"
              value={sourceGroups?.total ?? 0}
            >
              {Boolean(sourceGroups?.items.length) && (
                <SourcesSplit
                  sources={(sourceGroups?.items ?? []).map((item) => ({
                    chats: item.conversations,
                    id: item.id,
                    name: item.name,
                  }))}
                />
              )}
            </OverviewStatCard>
            {/* Organic vs Paid comes from the ad labels Meta puts on chats
                started from an ad; pages without that attribution get
                sourceSplit null and the card stays "Processing". New vs
                Returning still needs customer ids (HAP-502). */}
            <OverviewStatCard
              icon={OrganicVsPaidIcon}
              pending={!sourceSplit}
              size="compact"
              title="Organic vs Paid Chats"
              value={
                sourceSplit ? sourceSplit.organic + sourceSplit.paid : null
              }
            >
              <TwoWaySplit
                left={{
                  color: ORGANIC_COLOR,
                  count: sourceSplit?.organic ?? null,
                  label: 'Organic',
                }}
                right={{
                  color: PAID_COLOR,
                  count: sourceSplit?.paid ?? null,
                  label: 'Paid',
                }}
              />
            </OverviewStatCard>
            <OverviewStatCard
              badge={
                <TrendBadge
                  previousFromDate={comparison?.previousFromDate}
                  previousToDate={comparison?.previousToDate}
                  value={comparison?.totalUsers.changePercent}
                />
              }
              icon={TotalUsersIcon}
              pinValueToBottom
              size="compact"
              title="Total Users"
              value={data?.totalUsers.total ?? 0}
            />
            <OverviewStatCard
              icon={NewVsReturningIcon}
              pending
              size="compact"
              title="New vs Returning Users"
              value={null}
            >
              <TwoWaySplit
                left={{ color: NEW_COLOR, count: null, label: 'New' }}
                right={{
                  color: RETURNING_COLOR,
                  count: null,
                  label: 'Returning',
                }}
              />
            </OverviewStatCard>
            <TopSourceCard
              hasData={Boolean(topPositiveGroup || topNegativeGroup)}
              hideUnknownSourceIcon={activeTab === 'operators'}
              icon={TotalSourceIcon}
              mostNegative={
                topNegativeGroup
                  ? ({
                      negativeMentions: topNegativeGroup.metricValue,
                      source: topNegativeGroup.name,
                    } as any)
                  : undefined
              }
              mostPositive={
                topPositiveGroup
                  ? ({
                      positiveMentions: topPositiveGroup.metricValue,
                      source: topPositiveGroup.name,
                    } as any)
                  : undefined
              }
              title={activeTab === 'operators' ? 'Top Operator' : 'Top Source'}
              unitLabel={sourceUnitLabel}
            />
            <TopDayCard
              hasData={Boolean(positiveDay || negativeDay)}
              mostNegativeDay={
                negativeDay
                  ? {
                      label: negativeDay.day,
                      total: negativeDay.count,
                    }
                  : null
              }
              mostPositiveDay={
                positiveDay
                  ? {
                      label: positiveDay.day,
                      total: positiveDay.count,
                    }
                  : null
              }
              unitLabel="Chats"
            />
            <TopKeywordCard
              hasData={Boolean(positiveTopic || negativeTopic)}
              icon={TopicIcon}
              mostNegative={
                negativeTopic
                  ? ({
                      keyword: `# ${negativeTopic.name}`,
                      negativeMentions: negativeTopic.count,
                    } as any)
                  : undefined
              }
              mostPositive={
                positiveTopic
                  ? ({
                      keyword: `# ${positiveTopic.name}`,
                      positiveMentions: positiveTopic.count,
                    } as any)
                  : undefined
              }
              title="Top Topic"
              unitLabel="Chats"
            />
          </>
        )}
      </div>
    </div>
  )
}
